import { findChildren, findParentNodeClosestToPos, NodeWithPos } from '@tiptap/core'
import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { Node as ProseMirrorNode, Schema } from '@tiptap/pm/model'
import { EditorState, Plugin, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { LIST_TYPE_BULLET } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { ATTENDEE_LIST_NODE_NAME } from '../../../../../../backend/src/tiptap/editor/extensions/nodes/attendee-list'
import { isAuthoritativeContent } from './authoritative-content'
import { buildEmptyListItem, isListNode } from './list-track-changes'
import { LockedRange, placeChildren, touchesLockedRanges } from './locked-items-plugin'

/**
 * Holds the attendee list to the shape the minutes are exported in.
 *
 * The list is built from the meeting's agenda: a title, then one bulleted group per attendance category, each
 * naming itself and holding the people in it. Only the people are the reader's to change. The title, the labels
 * and the groups themselves are the shape the export reads, so an edit may only land inside a group, under its
 * label, and every list in there stays bulleted.
 *
 * A reader may still rest the caret on a label — they just cannot change what it says. Enter there means "add
 * someone here" rather than anything about the paragraph it sits in, so it starts a new person underneath.
 * A person is a flat entry under their label, so there is nothing to indent them into either, and emptying the
 * last of them takes their list with it — leaving the group in the same shape the minutes are generated with
 * when nobody is in that category.
 *
 * Nothing in the list carries an attribute saying which paragraph is a label, so its parts are found by
 * position. The schema is what makes that reliable rather than a guess: `attendee_list` is `paragraph
 * bulletList*`, so its first child is always the title, and `listItem` is `paragraph block*`, so a group's
 * first child is always its label. Those same rules already stop a title or label being removed outright, so
 * what is left to hold here is their text, the set of groups, and the list types.
 */

type AttendeeGroup = {
  /** The paragraph naming the group — "Present", "Apologies", "In attendance". */
  label: LockedRange
  /** Everything the group holds after its label, which is where its people are listed. */
  entries: LockedRange
}

type AttendeeStructure = {
  /** The title, shown read-only so the caret never lands in it. The labels are reachable; they just cannot be changed. */
  readOnly: LockedRange[]
  /** Everything in the list that is not a group's entries, so the parts an edit must leave alone. */
  fixed: LockedRange[]
}

type AttendeeStructurePluginState = {
  fixed: LockedRange[]
  decorations: DecorationSet
}

const attendeeStructurePluginKey = new PluginKey<AttendeeStructurePluginState>('attendeeStructure')

function isAttendeeListNode(node: ProseMirrorNode): boolean {
  return node.type.name === ATTENDEE_LIST_NODE_NAME
}

function findAttendeeList(doc: ProseMirrorNode): NodeWithPos | null {
  return placeChildren(doc, 0).find(({ node }) => isAttendeeListNode(node)) ?? null
}

function readGroup(group: NodeWithPos): AttendeeGroup | null {
  const label = group.node.firstChild
  if (label === null) {
    return null
  }
  const contentStart = group.pos + 1
  return {
    label: { from: contentStart, to: contentStart + label.nodeSize },
    entries: { from: contentStart + label.nodeSize, to: group.pos + group.node.nodeSize - 1 },
  }
}

function readGroups(attendeeList: NodeWithPos): AttendeeGroup[] {
  return placeChildren(attendeeList.node, attendeeList.pos + 1)
    .filter(({ node }) => isListNode(node))
    .flatMap((groupList) => placeChildren(groupList.node, groupList.pos + 1))
    .map(readGroup)
    .filter((group): group is AttendeeGroup => group !== null)
}

function readTitle(attendeeList: NodeWithPos): LockedRange[] {
  const title = attendeeList.node.firstChild
  if (title === null) {
    return []
  }
  return [{ from: attendeeList.pos + 1, to: attendeeList.pos + 1 + title.nodeSize }]
}

/**
 * The gaps the editable regions leave behind, so one overlap test covers the title, every label and the group
 * list's own shape without naming them separately.
 */
function findGapsAround(editable: LockedRange[], bounds: LockedRange): LockedRange[] {
  const starts = [bounds.from, ...editable.map((region) => region.to)]
  const ends = [...editable.map((region) => region.from), bounds.to]
  return starts.map((from, index) => ({ from, to: ends[index] })).filter((gap) => gap.from < gap.to)
}

export function findAttendeeStructure(doc: ProseMirrorNode): AttendeeStructure {
  const attendeeList = findAttendeeList(doc)
  if (attendeeList === null) {
    return { readOnly: [], fixed: [] }
  }

  const groups = readGroups(attendeeList)
  const bounds = { from: attendeeList.pos, to: attendeeList.pos + attendeeList.node.nodeSize }

  return {
    readOnly: readTitle(attendeeList),
    fixed: findGapsAround(
      groups.map((group) => group.entries),
      bounds
    ),
  }
}

/** The groups, then the people in them. The attendee list is two lists deep and no deeper. */
const ATTENDEE_LIST_DEPTH = 2

/** The longest run of nested lists inside a node. */
function measureListDepth(node: ProseMirrorNode): number {
  const depths: number[] = []
  node.forEach((child) => depths.push((isListNode(child) ? 1 : 0) + measureListDepth(child)))
  return Math.max(0, ...depths)
}

/**
 * Whether the attendee list's own lists are still the two bulleted levels it is made of.
 *
 * Neither half of this is something the range test above can see. A list-type toggle rewrites only the node's
 * own boundary tokens, and indenting a person nests a new list inside the entry they were on — both land
 * outside the ranges an edit is measured against, while changing the shape the export reads.
 */
function hasAllowedLists(doc: ProseMirrorNode): boolean {
  const attendeeList = findAttendeeList(doc)
  if (attendeeList === null) {
    return true
  }
  return (
    findChildren(attendeeList.node, isListNode).every(({ node }) => node.type.name === LIST_TYPE_BULLET) &&
    measureListDepth(attendeeList.node) <= ATTENDEE_LIST_DEPTH
  )
}

function keepsAttendeeStructure(transaction: Transaction, fixed: LockedRange[]): boolean {
  return !touchesLockedRanges(transaction, fixed) && hasAllowedLists(transaction.doc)
}

/** Strictly inside, so a caret at a node's own boundary counts as being outside it. */
function coversPosition(range: LockedRange, position: number): boolean {
  return position > range.from && position < range.to
}

function findGroupsOf(doc: ProseMirrorNode): AttendeeGroup[] {
  const attendeeList = findAttendeeList(doc)
  return attendeeList === null ? [] : readGroups(attendeeList)
}

function findGroupAtLabel(doc: ProseMirrorNode, position: number): AttendeeGroup | null {
  return findGroupsOf(doc).find(({ label }) => coversPosition(label, position)) ?? null
}

function findGroupAtEntries(doc: ProseMirrorNode, position: number): AttendeeGroup | null {
  return findGroupsOf(doc).find(({ entries }) => coversPosition(entries, position)) ?? null
}

/** The list of people under a group's label, once anyone has been added to it. */
function findEntriesList(doc: ProseMirrorNode, group: AttendeeGroup): NodeWithPos | null {
  const afterLabel = doc.nodeAt(group.entries.from)
  return afterLabel !== null && isListNode(afterLabel) ? { node: afterLabel, pos: group.entries.from } : null
}

/**
 * Starts a new person under the group label the caret is on, and puts the caret in it.
 *
 * The new entry goes directly beneath the label rather than at the end of the group, because that is where
 * Enter puts a new line everywhere else.
 */
export function addAttendeeUnderLabel(props: { state: EditorState; tr: Transaction; dispatch?: (tr: Transaction) => void }): boolean {
  const { state, tr, dispatch } = props
  const group = findGroupAtLabel(state.doc, state.selection.from)
  if (group === null) {
    return false
  }
  if (dispatch === undefined) {
    return true
  }

  const entry = buildEmptyListItem(state.schema)
  const entries = findEntriesList(state.doc, group)
  const insertion =
    entries === null ? { at: group.entries.from, node: state.schema.nodes[LIST_TYPE_BULLET].create(null, entry) } : { at: entries.pos + 1, node: entry }

  tr.insert(insertion.at, insertion.node)
  tr.setSelection(TextSelection.near(tr.doc.resolve(insertion.at), 1))
  return true
}

type EntryRemoval = {
  target: NodeWithPos
  /** Where the caret falls back to, the label being what sits above a group's first entry. */
  label: LockedRange
}

/** What Backspace takes away when `position` sits in the first entry under a label, rather than a later one. */
function findEmptyEntryRemoval(doc: ProseMirrorNode, position: number): EntryRemoval | null {
  const group = findGroupAtEntries(doc, position)
  const entries = group === null ? null : findEntriesList(doc, group)
  if (group === null || entries === null) {
    return null
  }

  const itemPos = entries.pos + 1
  const item = doc.nodeAt(itemPos)
  if (item === null || position >= itemPos + item.nodeSize) {
    return null
  }
  // The last person out takes the list with them, leaving the group as the minutes generate an empty one.
  return { target: entries.node.childCount === 1 ? entries : { node: item, pos: itemPos }, label: group.label }
}

/**
 * Backspace on an emptied first entry removes it, taking the list with it when nobody is left in the group.
 *
 * The usual handling merges the line into the one above, which for the first person under a label is the label
 * itself — an edit the guard rejects, leaving behind an empty bullet nothing could shift. Later entries are
 * left alone: they have a person above to merge into, which is an ordinary edit.
 *
 * With track changes on the name is still there under a deletion mark, so the entry is not empty and this
 * declines — the removal stays reviewable like any other edit.
 */
export function removeEmptyAttendeeEntry(props: { state: EditorState; tr: Transaction; dispatch?: (tr: Transaction) => void }): boolean {
  const { state, tr, dispatch } = props
  const { $from, empty } = state.selection
  if (!empty || $from.parent.content.size > 0) {
    return false
  }

  const removal = findEmptyEntryRemoval(state.doc, $from.pos)
  if (removal === null) {
    return false
  }
  if (dispatch === undefined) {
    return true
  }

  tr.delete(removal.target.pos, removal.target.pos + removal.target.node.nodeSize)
  // `to` sits after the label paragraph, so step back inside it before looking for the text.
  tr.setSelection(TextSelection.near(tr.doc.resolve(removal.label.to - 1), -1))
  return true
}

function hasAttendeeListAncestor(state: EditorState, position: number): boolean {
  return (findParentNodeClosestToPos(state.doc.resolve(position), isAttendeeListNode) ?? null) !== null
}

/** Whether the selection sits in the attendee list, whose lists are bulleted by definition. */
export function isAttendeeListSelected(state: EditorState): boolean {
  const { from, to } = state.selection
  return hasAttendeeListAncestor(state, from) || hasAttendeeListAncestor(state, to)
}

export function createAttendeeStructurePlugin(): Plugin<AttendeeStructurePluginState> {
  function build(doc: ProseMirrorNode): AttendeeStructurePluginState {
    const { readOnly, fixed } = findAttendeeStructure(doc)
    return {
      fixed,
      decorations: DecorationSet.create(
        doc,
        readOnly.map((range) => Decoration.node(range.from, range.to, { contenteditable: 'false' }))
      ),
    }
  }

  return new Plugin<AttendeeStructurePluginState>({
    key: attendeeStructurePluginKey,

    state: {
      init(_, state) {
        return build(state.doc)
      },
      apply(transaction, pluginState, _oldState, newState) {
        return transaction.docChanged ? build(newState.doc) : pluginState
      },
    },

    // A remote transaction carries an edit another client already made, and a regeneration replaces the list
    // wholesale through the same path; rejecting either would only push the two documents out of sync. A reset
    // hands back the server's own copy, which is not this reader's edit to judge either.
    filterTransaction(transaction, state) {
      if (isChangeOrigin(transaction) || isAuthoritativeContent(transaction) || !transaction.docChanged) {
        return true
      }
      const fixed = attendeeStructurePluginKey.getState(state)?.fixed ?? []
      return keepsAttendeeStructure(transaction, fixed)
    },

    props: {
      decorations(state) {
        return attendeeStructurePluginKey.getState(state)?.decorations ?? DecorationSet.empty
      },
    },
  })
}
